import React, { Component } from 'react';

import moment from 'moment';

import { AvatarCardAPI } from '@philips-emr/tws-components-api';
import { CardMode, CardType, StampIcon } from '@philips-emr/tws-components-api/lib/constants';
import { Color, Expression, Status } from '@philips-emr/tws-constants';
import { AwesomeComponent, ComponentType } from '@philips-emr/tws-core';
import { bool, eventListener, expression, instanceOf, number, oneOf, oneOfType, string } from '@philips-emr/tws-core/lib/component/prop-types';
import { msg } from '@philips-emr/tws-core/lib/services/message-service';
import { classNames, enumValues, isEnumValid, isValidWidth, trigger } from '@philips-emr/tws-utils';

import { Avatar, CardDate, Stamp } from '..';
import './avatar-card.scss';

@AwesomeComponent({ type: ComponentType.DATA_DISPLAY, api: AvatarCardAPI })
class AvatarCard extends Component {

  static propTypes = {
    color: oneOf(enumValues(Color)),
    date: oneOfType([string, instanceOf(moment)]),
    dateVisible: bool,
    image: string,
    label: expression,
    mode: oneOf(enumValues(CardMode)),
    onClick: eventListener,
    onContextMenuClick: eventListener,
    onDoubleClick: eventListener,
    stampIcon: oneOf(enumValues(StampIcon)),
    stampLabel: expression,
    stampVisible: bool,
    width: number,
    status: oneOf(enumValues(Status)),
    subtitle: expression,
    subtitleHiddenOnMobile: bool,
    title: expression,
    className: string.notEnumerable(),
    type: oneOf(enumValues(CardType)),
  };

  static defaultProps = {
    dateVisible: true,
    label: '',
    mode: CardMode.DEFAULT,
    stampLabel: '',
    stampVisible: true,
    type: CardType.DEFAULT,
    width: 3,
  };

  getStampIcon(status) {
    switch (status) {
      case Status.APPROVED:
      case Status.AVAILABLE:
      case Status.CONFIRMED:
      case Status.DONE:
      case Status.READY:
        return StampIcon.CHECK;
      case Status.NOT_APPROVED:
        return StampIcon.WARNING;
      case Status.CANCELED:
        return StampIcon.CANCELED;
      case Status.MISSED:
        return StampIcon.CANCELED_OUTLINE;
      case Status.AWAITING_APPROVAL:
      case Status.PROCESSING:
      case Status.WAITING:
        return StampIcon.CLOCK;
      default:
        return this.props.stampIcon;
    }
  }

  getStampColor(status) {
    switch (status) {
      case Status.CONFIRMED:
      case Status.AVAILABLE:
        return Color.SAFETY_GREEN;
      case Status.NOT_APPROVED:
        return Color.SAFETY_ORANGE;
      case Status.CANCELED:
        return Color.SAFETY_RED;
      case Status.DONE:
      case Status.MISSED:
      case Status.READY:
      case Status.APPROVED:
      case Status.WAITING:
      case Status.PROCESSING:
      case Status.AWAITING_APPROVAL:
        return Color.SAFETY_NEUTRAL;
      default:
        return this.props.color;
    }
  }

  getStampLabel(status) {
    const exps = {
      [Status.DONE]: Expression.DONE,
      [Status.READY]: Expression.READY,
      [Status.MISSED]: Expression.MISSED,
      [Status.WAITING]: Expression.WAITING,
      [Status.APPROVED]: Expression.APPROVED,
      [Status.CANCELED]: Expression.CANCELED,
      [Status.AVAILABLE]: Expression.AVAILABLE,
      [Status.CONFIRMED]: Expression.CONFIRMED,
      [Status.PROCESSING]: Expression.PROCESSING,
      [Status.NOT_APPROVED]: Expression.NOT_APPROVED,
      [Status.AWAITING_APPROVAL]: Expression.AWAITING_APPROVAL,
    };

    return exps[status] || this.props.stampLabel;
  }

  getStamp = () => {
    const { status, stampVisible } = this.props;

    if (!stampVisible) return null;

    const statusColor = this.getStampColor(status);
    const statusIcon = this.getStampIcon(status);
    const statusLabel = this.getStampLabel(status);

    return (
      <Stamp
        color={statusColor}
        icon={statusIcon}
        label={statusLabel} />
    );
  };

  getCardDate = () => {
    const { date, dateVisible, mode, type } = this.props;

    if (!dateVisible) return null;

    return (
      <CardDate
        date={date}
        divider={mode === CardMode.WIDE}
        type={type}
      />
    );
  };

  getAvatar = () => {
    const { direction, size } = this.getPreset();
    const { image, subtitle, subtitleHiddenOnMobile, title } = this.props;

    const sizeBreakpoints = ['gxsm'];
    const directionBreakpoints = ['gxsm'];

    return (
      <Avatar
        direction={direction}
        directionBreakpoints={directionBreakpoints}
        image={image}
        size={size}
        sizeBreakpoints={sizeBreakpoints}
        subtitle={subtitle}
        subtitleHiddenOnMobile={subtitleHiddenOnMobile}
        title={title}
      />
    );
  };

  getPreset = () => {
    const { mode } = this.props;

    return mode === CardMode.WIDE
      ? { direction: '', size: '' }
      : { direction: 'vertical', size: 'xlg' };
  };

  getRootContainerClasses = () => {
    const { className, color = '', dateVisible, mode, status, type, width } = this.props;

    const rootContainerClasses = {
      'c-avatar-card': true,
      'c-avatar-card--no-date': !dateVisible,
      [`u-grid-col-${width}`]: isValidWidth(width),
      [`c-avatar-card--${type}`]: type === CardType.FEATURED,
      [`c-avatar-card--${mode}`]: mode === CardMode.WIDE,
      [`is-${status}`]: isEnumValid(Status, status),
      [`u-color-${color.replace('_', '-')}`]: isEnumValid(Color, color),
      [className]: Boolean(className),
    };

    return classNames(rootContainerClasses);
  };

  render() {
    const { label, onClick, onDoubleClick, onContextMenuClick } = this.props;

    return (
      <div
        className={this.getRootContainerClasses()}
        x-contrast="cl bd"
        onClick={trigger(onClick)}
        onDoubleClick={trigger(onDoubleClick)}
        onContextMenu={trigger(onContextMenuClick)}
      >

        {label && <span className="c-avatar-card__label u-hide@gxsm">{msg(label)}</span>}

        <div className="c-avatar-card__wrapper">

          <div className="c-avatar-card__date">
            {this.getCardDate()}
          </div>

          <div className="c-avatar-card__avatar">
            {this.getAvatar()}
          </div>

          <div className="c-avatar-card__stamp">
            {this.getStamp()}
          </div>

        </div>
      </div>
    );
  }
}

export default AvatarCard;
